import {Nullable} from '../../../app/TypeHelpers'
import {CHARGE_UNIT, PAYMENT_TYPE, UNIT_TIME} from '../../../CodeMaster'
import ReservePaymentCalculator from './ReservePaymentCalculator'
import {ReserveOptionPaymentCalculator} from './ReserveOptionPaymentCalculator'
import moment from 'moment'
import {BusinessScheduleWithDateTime} from '../ReserveFacilityPaymentModel'
import {calculateDurations} from '../helpers/helpers'
import {BusinessSchedule} from '../../facilities/FacilityForReservation'
import {PaymentItemForViewDuration} from '../element/PaymentItemForViewDuration'
import {PaymentItem} from '../ReserveFacilityModel'

export interface ReservationPeriod {
    start: moment.Moment
    end: moment.Moment
}

export abstract class ReservePaymentCalculatorDuration extends ReservePaymentCalculator {

    protected readonly businessSchedules: BusinessSchedule[] | BusinessScheduleWithDateTime[] = []
    protected readonly startMoment: Nullable<moment.Moment> = null
    protected readonly endMoment: Nullable<moment.Moment> = null

    constructor(
        businessSchedules: BusinessSchedule[] | BusinessScheduleWithDateTime[],
        startMoment: Nullable<moment.Moment>,
        endMoment: Nullable<moment.Moment>,
        optionCalculator: ReserveOptionPaymentCalculator,
    ) {
        super(optionCalculator)
        this.businessSchedules = businessSchedules
        this.startMoment = startMoment
        this.endMoment = endMoment
    }

    protected calculateFacilityPayment(): PaymentItem {
        if (this.startMoment === null || this.endMoment === null) {
            throw Error('this.startMoment & this.endMoment cannot be null')
        }

        return new PaymentItemForViewDuration(
            PAYMENT_TYPE.ADMIN_TRANSFER_ACCOUNT,
            this.calculateFacilityChargeByDuration(this.startMoment, this.endMoment),
            this.startMoment,
            this.endMoment,
        ).toObject()
    }

    private calculateFacilityChargeByDuration(
        startMoment: moment.Moment,
        endMoment: moment.Moment,
    ): number {
        if (!endMoment.isAfter(startMoment)) {
            return 0
        }

        return (this.businessSchedules as BusinessScheduleWithDateTime[])
            .map(schedule => {
                const {start, end} = this.adjustPeriodBySchedule(
                    schedule, {start: startMoment, end: endMoment},
                )

                if (schedule.chargeUnit === CHARGE_UNIT.FIXED) {
                    return this.getCharge(schedule)
                }

                return this.calculateFacilityChargeByPeriod(schedule, start, end)
            })
            .reduce((total, charge) => total + charge)
    }

    private getCharge(schedule: BusinessScheduleWithDateTime): number {
        if (schedule.charge === null) {
            throw Error('schedule.charge cannot be null')
        }
        return schedule.charge
    }

    private getChangedCharge(schedule: BusinessScheduleWithDateTime): number {
        if (schedule.changedCharge === null) {
            throw  Error('schedule.changedCharge cannot be null')
        }
        return schedule.changedCharge
    }

    private calculateFacilityChargeByPeriod(
        schedule: BusinessScheduleWithDateTime,
        startMoment: moment.Moment,
        endMoment: moment.Moment,
    ): number {
        const startTimeNormal: moment.Moment = startMoment
        let endTimeNormal: moment.Moment = endMoment
        let startTimeOver: Nullable<moment.Moment> = null
        let endTimeOver: Nullable<moment.Moment> = null

        if (schedule.changedChargeApplyStartTime !== null) {
            let changedChargeApplyStartMinutes: number
            if (schedule.changedChargeApplyStartTimeUnit === UNIT_TIME.HOUR) {
                changedChargeApplyStartMinutes = schedule.changedChargeApplyStartTime * 60
            } else {
                changedChargeApplyStartMinutes = schedule.changedChargeApplyStartTime
            }

            const durationInMillisecond = endMoment.diff(startMoment)
            const durationInMinute: number = Math.ceil(durationInMillisecond / 1000 / 60)
            if (changedChargeApplyStartMinutes < durationInMinute) {
                endTimeNormal = startMoment.clone().add(changedChargeApplyStartMinutes, 'minutes')
                startTimeOver = endTimeNormal
                endTimeOver = endMoment
            }
        }
        const facilityCharge = this.calculateFacilityCharge(schedule, startTimeNormal, endTimeNormal)
        const facilityChangedCharge = this.calculateFacilityChangedCharge(
            startTimeOver, endTimeOver, schedule)
        return facilityCharge + facilityChangedCharge
    }

    protected abstract adjustPeriodBySchedule(
        schedule: BusinessScheduleWithDateTime | BusinessSchedule,
        period: ReservationPeriod,
    ): ReservationPeriod

    isEnableCalculate(): boolean {
        if (this.startMoment === null || this.endMoment === null) {
            return false
        }

        if (!this.endMoment.isAfter(this.startMoment)) {
            return false
        }

        if (this.businessSchedules === null) {
            return false
        }

        // noinspection RedundantIfStatementJS
        if (this.businessSchedules.length === 0) {
            return false
        }

        return true
    }

    private calculateFacilityCharge = (
        schedule: BusinessScheduleWithDateTime,
        startMoment: moment.Moment,
        endMoment: moment.Moment): number => {
        if (schedule.chargeUnitTime === null) {
            throw Error('schedule.chargeUnitTime cannot be null')
        }

        const {durationInMinute, durationInHour} = calculateDurations(startMoment, endMoment)

        const chargeCount = schedule.chargeUnit === CHARGE_UNIT.TIME
            ? Math.ceil(durationInHour / schedule.chargeUnitTime)
            : Math.ceil(durationInMinute / schedule.chargeUnitTime)

        return chargeCount * this.getCharge(schedule)
    }

    private calculateFacilityChangedCharge = (
        startMoment: Nullable<moment.Moment>,
        endMoment: Nullable<moment.Moment>,
        schedule: BusinessScheduleWithDateTime): number => {
        if (startMoment === null || endMoment === null) {
            return 0
        }

        if (!endMoment.isAfter(startMoment)) {
            return 0
        }

        if (schedule.changedCharge === null) {
            throw  Error('schedule.changedCharge cannot be null')
        }

        const changedCharge = this.getChangedCharge(schedule)
        if (schedule.changedChargeUnit === CHARGE_UNIT.FIXED) {
            return changedCharge
        }

        if (schedule.changedChargeUnitTime === null) {
            throw  Error('changedChargeUnitTime cannot be null')
        }

        const {durationInMinute, durationInHour} = calculateDurations(startMoment, endMoment)

        const chargeCount = schedule.changedChargeUnit === CHARGE_UNIT.TIME
            ? Math.ceil(durationInHour / schedule.changedChargeUnitTime)
            : Math.ceil(durationInMinute / schedule.changedChargeUnitTime)

        return chargeCount * changedCharge
    }
}
